/**
 * AffaireHeader — Dossier header with stage badge and actions
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/core/auth';
import { apiClient } from '@/core/api/client';
import type { Affaire } from '@/features/admin/tabs/search/types/search.types';
import { getStageLabel } from '../utils/formatters';
import { STAGE_COLORS } from '../utils/constants';

interface AffaireHeaderProps {
  affaire: Affaire;
  onOpenTracker?: () => void;
}

export const AffaireHeader: React.FC<AffaireHeaderProps> = ({ affaire, onOpenTracker }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const isNewDossier = parseInt(affaire.id_unique) >= 90000;

  const PIPELINE_STAGES = [
    'Bureau à encoder', 'Bureau à planifier', 'RV en attente',
    'Expert à visiter', 'Expert à achever', 'Bureau à valoriser',
    'Bureau à achever', 'Attente doc/infos', 'Attente paiement',
    'Attente confirmation', 'À transmettre', 'ARCHIVES', 'SANS SUITE',
  ];

  const handleStatusChange = async (newStatus: string) => {
    try {
      await apiClient.put(`/api/dossiers/${affaire.id_unique}/status`, { status: newStatus });
      (affaire as any).statut_affaire = newStatus;
      setShowStatusMenu(false);
      window.location.reload();
    } catch (e) { console.error('Status change failed:', e); }
  };

  const handleGenerateReport = async (reportType: string) => {
    setGenerating(true);
    try {
      const res: any = await apiClient.post(`/api/reports/${affaire.id_unique}/generate`, { report_type: reportType });
      if (res.success && res.data.downloadUrl) {
        // Fetch PDF with auth then open in new tab
        const token = localStorage.getItem('authToken');
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const pdfRes = await fetch(`${baseUrl}${res.data.downloadUrl}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const blob = await pdfRes.blob();
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        window.open(url, '_blank');
      }
    } catch (err: any) {
      console.error('Report generation failed:', err);
      const msg = err?.response?.data?.message || 'Erreur lors de la génération du rapport';
      if (msg.includes('token') || msg.includes('Token')) {
        alert('Session expirée. Veuillez vous reconnecter.');
      } else {
        alert(msg);
      }
    } finally {
      setGenerating(false);
    }
  };

  const stageLabel = getStageLabel(affaire.statut_affaire || '');
  const colors = STAGE_COLORS[stageLabel] || { bg: 'bg-gray-100', text: 'text-gray-700' };

  const clientName = affaire.client
    ? `${affaire.client.firstName || ''} ${affaire.client.lastName || ''}`.trim()
    : '';

  return (
    <div className="aff-detail-header">
      <div className="aff-detail-header-left">
        <button
          onClick={() => navigate(-1)}
          className="aff-detail-back-btn"
          title="Retour"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>

        <div className="aff-detail-title-group">
          <h1 className="aff-detail-title">
            Dossier #{affaire.id_unique}
          </h1>
          {clientName && (
            <>
              <span className="aff-detail-title-sep">&mdash;</span>
              <span className="aff-detail-client-name">{clientName}</span>
            </>
          )}
        </div>

        <span className={`aff-detail-stage-badge ${colors.bg} ${colors.text}`}
          style={isNewDossier ? { cursor: 'pointer', position: 'relative' } : undefined}
          onClick={() => isNewDossier && setShowStatusMenu(!showStatusMenu)}>
          {stageLabel}
          {isNewDossier && <span style={{ marginLeft: 4, fontSize: 10 }}>▼</span>}
        </span>
        {showStatusMenu && (
          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 4, minWidth: 200 }}>
            {PIPELINE_STAGES.map(s => (
              <button key={s} onClick={() => handleStatusChange(s)}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: s === stageLabel ? '#FFF8F8' : 'none', color: s === stageLabel ? '#EF5343' : '#334155', fontSize: 12, cursor: 'pointer', borderRadius: 4, fontWeight: s === stageLabel ? 600 : 400 }}>
                {s}
              </button>
            ))}
          </div>
        )}

        {affaire.type_mission && (
          <span className="aff-detail-type-badge">
            {affaire.type_mission}
          </span>
        )}
      </div>

      <div className="aff-detail-header-actions">
        {isAdmin && (
          <div className="aff-detail-report-actions">
            <button
              onClick={() => handleGenerateReport('provisoire')}
              disabled={generating}
              className="aff-detail-action-btn aff-detail-action-btn--primary"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              {generating ? 'Génération...' : 'Rapport provisoire'}
            </button>
            <button
              onClick={() => handleGenerateReport('definitif')}
              disabled={generating}
              className="aff-detail-action-btn"
            >
              Rapport définitif
            </button>
          </div>
        )}
        {onOpenTracker && (
          <button onClick={onOpenTracker} className="aff-detail-action-btn">
            Tracker
          </button>
        )}
        <a
          href={`https://fm2.chatelainxpert.be/fmi/webd/ChatelainXpert?script=OpenAffaire&param=${affaire.id_unique}`}
          target="_blank"
          rel="noopener noreferrer"
          className="aff-detail-action-btn"
        >
          FileMaker
        </a>
      </div>
    </div>
  );
};
